import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
    PAGINATION_PAGE_PARAM,
    PAGINATION_PER_PAGE_PARAM,
    SearchParamController,
} from '@clockout-dev/design-elements';
import ClockoutController from '../../core/clockout.controller';
import Inherit from '../../core/inherit';
import TeamDelegate from './team.delegate';
import { DEFAULT_PAGE_SIZE } from './team.constants';
import { SCOPES } from '../../constants/scopes.constants';

dayjs.extend(relativeTime);

class TeamController extends Inherit(ClockoutController, TeamDelegate, SearchParamController) {
    state = {
        user: {},
        loading: true,
        users: [],
        usersPageInfo: { current: 1, perPage: DEFAULT_PAGE_SIZE },
        fiRoles: [],
        newMemberEmail: '',
        // Holds a roles.id (not a role name) — this FI's own custom roles,
        // fetched from GET /api/roles/:aba, same source as the Role Composer.
        newMemberRole: null,
        inviteLoading: false,
        usersListLoading: false,
        inviteError: '',
    };

    /** Can send new invitations. Gates the invite rail. Mirrors the scope
     * `sendInvitation.js` actually checks server-side. */
    get canInvite() {
        return this.hasScope(SCOPES.TEAM_INVITE);
    }

    /** Gates the resend-invitation menu item. */
    get canManageTeam() {
        return this.hasScope(SCOPES.TEAM_MANAGE);
    }

    /** Can change a member's assigned role. Gates the change-role submenu. */
    get canChangeUserRole() {
        return this.hasScope(SCOPES.TEAM_CHANGE_ROLE);
    }

    /** Can revoke or restore a member's access to this FI. */
    get canRemoveAccess() {
        return this.hasScope(SCOPES.TEAM_REMOVE_ACCESS);
    }

    /** Can send a password reset email to a member. */
    get canResetPassword() {
        return this.hasScope(SCOPES.TEAM_RESET_PASSWORD);
    }

    /** Can assign the FI's owner-equivalent role to someone else. */
    get canAssignOwnerRole() {
        return this.hasScope(SCOPES.ASSIGN_OWNER_ROLE);
    }

    /** Roles assignable from the invite form / change-role menu — every FI
     * role, minus the owner-equivalent one unless the current user is
     * allowed to assign it (server enforces the same rule independently). */
    getAssignableRoles() {
        const { fiRoles } = this.state;
        return fiRoles
            .filter((role) => !role.is_owner_role || this.canAssignOwnerRole)
            .map((role) => ({ value: role.id, label: role.name }));
    }

    getRoleName(roleId) {
        return this.state.fiRoles.find((role) => role.id === roleId)?.name || 'Unknown';
    }

    async initialize() {
        // Check if user has valid data
        if (!this.user || !this.user.access || this.user.access.length === 0) {
            this.navigateLogin();
            return;
        }

        const user = this.data.user;
        const { initialPage, initialPerPage } = this.resolveInitialPagination();
        this.setState({
            user,
            loading: false,
            usersPageInfo: { current: initialPage, perPage: initialPerPage, total: 0 },
        });
        if (user?.fi) {
            await this.loadRoles();
            await this.loadUsers(initialPage, initialPerPage);
        }
    }

    async loadRoles() {
        const fiRoles = await this.data.fetchRoles();
        const defaultRole = fiRoles.find((role) => role.is_default_role);
        this.setState((prev) => ({
            fiRoles,
            newMemberRole: prev.newMemberRole || defaultRole?.id || fiRoles[0]?.id || null,
        }));
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevState.user?.fi !== this.state.user?.fi && this.state.user?.fi) {
            const { initialPage, initialPerPage } = this.resolveInitialPagination();
            this.loadUsers(initialPage, initialPerPage);
        }
    }

    /**
     * Reads `page` / `perPage` from the URL (written by the package
     * Table's `saveSearchParams`) so a refresh lands on the same slice.
     */
    resolveInitialPagination() {
        const rawPage = parseInt(this.getSearchParamValue(PAGINATION_PAGE_PARAM), 10);
        const rawPerPage = parseInt(this.getSearchParamValue(PAGINATION_PER_PAGE_PARAM), 10);
        return {
            initialPage: Number.isFinite(rawPage) && rawPage > 0 ? rawPage : 1,
            initialPerPage: Number.isFinite(rawPerPage) && rawPerPage > 0
                ? rawPerPage
                : DEFAULT_PAGE_SIZE,
        };
    }

    async loadUsers(page = 1, perPage = DEFAULT_PAGE_SIZE) {
        try {
            this.setState({ usersListLoading: true });
            const result = await this.data.fetchAdminUsers(undefined, { page, pageSize: perPage });
            const items = result?.items || [];
            const total = result?.pageinfo?.count ?? items.length;
            this.setState({
                users: items,
                usersPageInfo: { current: page, perPage, total },
            });
        } catch (error) {
            console.error('Error loading users:', error);
        } finally {
            this.setState({ usersListLoading: false });
        }
    }

    handleUsersTableChange(pagination) {
        this.loadUsers(pagination?.current || 1, pagination?.pageSize || DEFAULT_PAGE_SIZE);
    }

    resetInviteForm() {
        const defaultRole = this.state.fiRoles.find((role) => role.is_default_role);
        this.setNewMemberEmail('');
        this.setNewMemberRole(defaultRole?.id || this.state.fiRoles[0]?.id || null);
        this.setInviteError('');
    }

    handleInviteSubmit = (e) => {
        e.preventDefault();
        if (this.state.inviteLoading) return;
        this.handleInviteMember();
    };

    handleInviteMember = async () => {
        if (!this.canInvite) return;
        const { newMemberEmail, newMemberRole: newMemberRoleId } = this.state;

        if (!newMemberEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newMemberEmail)) {
            this.setInviteError('Please enter a valid email address.');
            return;
        }
        if (!newMemberRoleId) {
            this.setInviteError('Please select a role.');
            return;
        }

        const lowerCaseEmail = newMemberEmail.toLowerCase();
        const roleName = this.getRoleName(newMemberRoleId);

        try {
            this.setState({ inviteLoading: true, inviteError: '' });
            const result = await this.data.sendInvitation({
                email: lowerCaseEmail,
                roleId: newMemberRoleId,
            });

            if (result && result.success) {
                this.resetInviteForm();
                const { usersPageInfo } = this.state;
                await this.loadUsers(usersPageInfo.current, usersPageInfo.perPage);
                this.props.notificationApi.success({
                    message: 'Invitation sent',
                    description: `Invitation sent to ${lowerCaseEmail} as ${roleName}.`,
                });
            } else {
                this.setInviteError(result?.message || 'Failed to send invitation. Please try again.');
                this.props.notificationApi.error({
                    message: 'Invitation failed',
                    description: result?.message || 'Failed to send invitation. Please try again.',
                });
            }
        } catch (error) {
            console.error('Error sending invitation:', error);
            this.setInviteError('An error occurred while sending the invitation. Please try again.');
            this.props.notificationApi.error({
                message: 'Invitation failed',
                description: 'An error occurred while sending the invitation. Please try again.',
            });
        } finally {
            this.setState({ inviteLoading: false });
        }
    };

    handleDeleteUser = async (userId, isCurrentlyActive) => {
        if (!this.canRemoveAccess) return;
        try {
            await this.data.removeUserAccess(userId);
            const { usersPageInfo } = this.state;
            await this.loadUsers(usersPageInfo.current, usersPageInfo.perPage);

            if (isCurrentlyActive) {
                // Removing access
                this.props.notificationApi.success({
                    message: 'Access removed',
                    description: 'The team member can no longer access this financial institution.',
                });
            } else {
                // Granting access
                this.props.notificationApi.success({
                    message: 'Access granted',
                    description: 'The team member now has access to this financial institution.',
                });
            }
        } catch (error) {
            console.error('Error toggling user access:', error);
            const description = error?.response?.data?.error
                || 'Failed to change user access. Please try again.';
            const message = isCurrentlyActive ? 'Remove access failed' : 'Grant access failed';
            this.props.notificationApi.error({
                message,
                description,
            });
        }
    };

    handleChangeRole = async (userId, roleId) => {
        if (!this.canChangeUserRole) return;
        try {
            await this.data.changeUserRole(userId, roleId);
            const { usersPageInfo } = this.state;
            await this.loadUsers(usersPageInfo.current, usersPageInfo.perPage);
            this.props.notificationApi.success({
                message: 'Role updated',
                description: `The team member is now a ${this.getRoleName(roleId)}.`,
            });
        } catch (error) {
            console.error('Error changing user role:', error);
            const description = error?.response?.data?.error
                || 'Failed to change the role. Please try again.';
            this.props.notificationApi.error({
                message: 'Role change failed',
                description,
            });
        }
    };

    handleSendPasswordReset = async (email) => {
        if (!this.canResetPassword) return;
        try {
            await this.data.sendPasswordReset(email);
            this.props.notificationApi.success({
                message: 'Password reset sent',
                description: `Password reset email sent to ${email}. They can update their password via the reset link.`,
            });
        } catch (error) {
            console.error('Error sending password reset:', error);
            const description = error?.response?.data?.error
                || 'Failed to send password reset email. Please try again.';
            this.props.notificationApi.error({
                message: 'Password reset failed',
                description,
            });
        }
    };

    handleResendInvitation = async (email, roleId) => {
        if (!this.canManageTeam) return;
        try {
            await this.data.sendInvitation({ email, roleId });
            const { usersPageInfo } = this.state;
            await this.loadUsers(usersPageInfo.current, usersPageInfo.perPage);
            this.props.notificationApi.success({
                message: 'Invitation resent',
                description: `Invitation resent to ${email} as ${this.getRoleName(roleId)}.`,
            });
        } catch (error) {
            console.error('Error resending invitation:', error);
            const description = error?.response?.data?.error
                || 'Failed to resend invitation. Please try again.';
            this.props.notificationApi.error({
                message: 'Resend failed',
                description,
            });
        }
    };

    getUserRole = (user) => {
        return this.getRoleName(user.roleId);
    };

    getUserStatus = (user) => {
        if (user.invited && !user.active) return 'Invited';
        return user.active ? 'Active' : 'Inactive';
    };

    formatLastActivity = (user) => {
        // Backend maps last_login_at → lastActivity (fetchAdminUsersByAba.js).
        const timestamp = user.lastActivity;
        if (!timestamp) return '—';

        const lastActivity = dayjs(timestamp);
        return lastActivity.isValid() ? lastActivity.fromNow() : '—';
    };
}

export default TeamController;
